import React, {
  useEffect,
  useMemo,
} from 'react';
import {
  useFormContext,
} from 'react-hook-form';
import isNil from 'lodash/isNil';
import defer from 'lodash/defer';
import Controller from 'core/components/FormController';
import Input from 'pages/Dashboard/components/Input';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import {
  validatePhoneIfSet,
  normalizePhoneNumber,
  validateEmailIfSet,
} from 'pages/Dashboard/utils/helper';
import useConditionalRequirementCallback from 'pages/Dashboard/pages/Appointments/pages/List/components/CreatePatientDialog/useConditionalRequirement';
import omit from 'lodash/omit';
import AddressFields from 'pages/Dashboard/components/AddressFields';
import SwitchButton from 'core/components/Switch';
import {
  addressRequiredFieldsNotEmpty,
  validateAddressInfo,
} from 'utils/react-hook-form';
import ToolTip from 'core/components/Tooltip';
import {
  isEmptyString,
} from '@populateui/utils';
import MultiSelect from 'core/components/Multiselect';
import keys from 'lodash/keys';
import Pill from 'core/components/Pill';
import PhoneInputWrapper from 'core/components/PhoneInputWrapper';

const contactRoles = {
  Caregiver: 'Caregiver',
  Guardian: 'Guardian',
  Translator: 'Translator',
  EmergencyContact: 'Emergency Contact',
  Other: 'Other',
};
const roleOptions = keys(contactRoles);
type OptionKey = keyof typeof contactRoles;

export default function OtherContactFields() {
  const {
    control,
    watch,
    trigger,
    clearErrors,
    setValue,
  } = useFormContext();
  const [otherContact, updatedOn, patientId] = watch(['otherContact', 'updatedOn', 'patientId']);
  const {
    roles,
    address,
    phoneNumber,
    email,
    contactName,
    isBillingContact,
  } = otherContact ?? {};

  const otherContactInfo = useMemo(
    () => omit({
      ...otherContact,
      roles: otherContact?.roles?.length > 0 ? otherContact?.roles : null,
      address: !isNil(address) && addressRequiredFieldsNotEmpty(address) ? address : '',
      phoneNumber: phoneNumber === '+1'
        ? '' : normalizePhoneNumber(phoneNumber),
    }, 'patientId'),
    [contactName, email, phoneNumber, address, roles],
  );

  const validateRequiredIfOtherSet = useConditionalRequirementCallback(otherContactInfo);

  const isValidOtherContactInfo = useMemo(() => {
    const isAddressValid = !isNil(address)
      && validateAddressInfo(address)
      && addressRequiredFieldsNotEmpty(address);
    return validateRequiredIfOtherSet(contactName) && isAddressValid;
  }, [contactName, address]);

  useEffect(() => {
    trigger('otherContact.contactName');
  }, [otherContactInfo, trigger]);

  useEffect(() => {
    trigger('otherContact.roles');
  }, [otherContactInfo, trigger]);

  useEffect(() => {
    trigger('otherContact.address');
  }, [address, trigger]);

  useEffect(() => {
    if (isBillingContact && (
      !addressRequiredFieldsNotEmpty(address)
      || isEmptyString(contactName)
    )) {
      setValue('otherContact.isBillingContact', false);
    }
  }, [roles, contactName, address]);

  const handleChange = (
    fieldOnChange: (...event: any[]) => void,
    ...event: any[]
  ) => {
    defer(async () => {
      const isValid = await trigger('otherContact.contactName');
      if (isValid) {
        clearErrors('otherContact.contactName');
      }
    });

    fieldOnChange(...event);
  };

  return (
    <div className="flex flex-col gap-form-row">
      <div className="flex justify-between gap-form-field">
        <Controller
          name="otherContact.contactName"
          control={control}
          rules={{ validate: validateRequiredIfOtherSet }}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              error={!isNil(error)}
              showErrorText={false}
              required={!isNil(error) || !isEmptyString(field.value)}
              className="flex-1 min-w-0"
              id="name-of-other-contact"
              labelClasses="font-semibold text-xs text-gray-500"
              label="Full Name"
              placeholder="Full Name"
            />
          )}
        />
        <Controller
          name="otherContact.phoneNumber"
          control={control}
          rules={{ validate: validatePhoneIfSet }}
          render={({ field, fieldState: { error } }) => (
            <Wrapper
              id="other-contact-phone-number"
              label="Phone"
              labelClasses="font-semibold text-xs text-gray-500"
              className="flex-1 min-w-0"
              error={!isNil(error)}
              showErrorText={false}
            >
              <PhoneInputWrapper
                {...field}
                error={!isNil(error)}
                onChange={(...event: any[]) => {
                  handleChange(field.onChange, ...event);
                }}
                id="other-contact-phone-number"
                name="other-contact-phone-number"
                placeholder="Phone"
                fieldKey="otherContact.phoneNumber"
              />
            </Wrapper>
          )}
        />
        <Controller
          name="otherContact.email"
          control={control}
          rules={{ validate: validateEmailIfSet }}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              onChange={(...event: any[]) => {
                handleChange(field.onChange, ...event);
              }}
              className="flex-1 min-w-0"
              id="other-contact-email"
              labelClasses="font-semibold text-xs text-gray-500"
              type="email"
              label="Email"
              error={!isNil(error)}
              showErrorText={false}
              placeholder="Email"
            />
          )}
        />
      </div>
      <Controller
        key={`other-contact-address-${updatedOn}`}
        control={control}
        rules={{ validate: (value) => (validateAddressInfo(value) || 'Provide valid address') }}
        name="otherContact.address"
        render={({ field }) => (
          <AddressFields {...field} />
        )}
      />
      <Controller
        name="otherContact.roles"
        key={`other-contact-roles-${patientId}`}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Wrapper
            id="role"
            label="Role"
            error={!isNil(error)}
            showErrorText={false}
          >
            <MultiSelect
              {...field}
              options={roleOptions}
              id="role"
              className="w-1/3"
              renderOption={(option) => contactRoles[option as OptionKey]}
              getItemLabel={(option) => contactRoles[option as OptionKey]}
              renderValue={(onRemove, value) => (
                <div className="flex flex-wrap gap-2">
                  {value.length > 0 ? value.map((option) => (
                    <Pill
                      key={option}
                      id={option}
                      label={contactRoles[option as OptionKey]}
                      onRemove={(id) => {
                        onRemove?.(id);
                        field.onBlur?.();
                      }}
                    />
                  )) : (
                    <div className="text-gray-400">Select role</div>
                  )}
                </div>
              )}
            />
          </Wrapper>
        )}
      />
      <Controller
        name="otherContact.isBillingContact"
        key={`other-contact-is-billing-contact-${patientId}`}
        control={control}
        render={({ field }) => (
          <ToolTip
            hide={isValidOtherContactInfo}
            title="Contact name or address is missing."
          >
            <span className="max-w-max">
              <SwitchButton
                {...field}
                label="Bill to this contact"
                checked={field.value ?? false}
                disabled={!isValidOtherContactInfo}
                classes={{
                  rootContainer: '!gap-1',
                  label: '!mt-0.5 !font-medium !text-gray-600',
                }}
              />
            </span>
          </ToolTip>
        )}
      />
    </div>
  );
}
